import React, { ReactNode, useEffect, useState } from "react";
import { InputAdornment, TextField, Theme, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CustomTypography from "../form/CustomTypography";
import {
  blockEdgeSpaces,
  inputFromKeyEvent,
  isEdgeSpaceKey,
} from "../../../../utils/blockEdgeSpaces";

interface CustomTextFieldProps {
  value?: any;
  placeholder?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onSearch?: (query: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  width?: string | number;
  borderRadius?: string | number;
  customStyles?: React.CSSProperties;
  fontSize?: string | number;
  disabled?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode | null;
  label?: string;
  onIconClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: () => void;
  borderColor?: string;
  type?: "text" | "number";
  error?: boolean;
  allowNegativeValue?: boolean;
  allowDecimalValue?: boolean;
  helperText?: string;
  preventEdgeSpaces?: boolean;
}

const CustomTextField: React.FC<CustomTextFieldProps> = ({
  value = "",
  placeholder = "Search...",
  onChange,
  onBlur,
  onSearch,
  onKeyDown,
  width = "240px",
  borderRadius = "4px",
  customStyles = {},
  fontSize = "0.8rem",
  startAdornment,
  endAdornment = null,
  disabled = false,
  onIconClick,
  onClick,
  borderColor,
  type = "text",
  allowNegativeValue = true,
  allowDecimalValue = true,
  helperText = "",
  preventEdgeSpaces = false,
}) => {
  const theme = useTheme<Theme>();

  const [query, setQuery] = useState(value || "");

  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
    }
  }, [value]);

  const isValidNumberInput = (input: string): boolean => {
    if (type !== "number") return true;

    const regex = allowNegativeValue ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;

    if (!regex.test(input)) {
      return false;
    }

    if (
      (input.match(/-/g) || []).length > 1 ||
      (input.includes("-") && !input.startsWith("-"))
    ) {
      return false;
    }

    if (!allowNegativeValue && input.includes("-")) {
      return false;
    }

    if (!allowDecimalValue && input.includes(".")) {
      return false;
    }

    return true;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const newValue = preventEdgeSpaces ? blockEdgeSpaces(rawValue) : rawValue;

    if (type === "number" && !isValidNumberInput(newValue)) {
      return;
    }

    if (newValue !== rawValue) {
      event.target.value = newValue;
    }

    setQuery(newValue);
    onSearch?.(newValue);
    onChange?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number" && !allowNegativeValue && event.key === "-") {
      event.preventDefault();
    }
    const input = inputFromKeyEvent(event);
    if (
      preventEdgeSpaces &&
      input &&
      isEdgeSpaceKey(
        event.key,
        input.value,
        input.selectionStart,
        input.selectionEnd,
      )
    ) {
      event.preventDefault();
      return;
    }
    onKeyDown?.(event);
  };

  return (
    <div style={{ width, ...customStyles }}>
      <TextField
        type={type}
        value={query}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onClick={onClick}
        placeholder={placeholder}
        fullWidth
        disabled={disabled}
        InputProps={{
          startAdornment,
          endAdornment: endAdornment ? (
            <InputAdornment position="end">
              <SearchIcon sx={{ color: "#A3A3A3", fontSize: "1.2rem" }} />
            </InputAdornment>
          ) : (
            endAdornment
          ),
          inputProps: {
            ...(type === "number" && {
              pattern: allowNegativeValue
                ? allowDecimalValue
                  ? "-?[0-9]*\\.?[0-9]*"
                  : "-?[0-9]*"
                : allowDecimalValue
                ? "[0-9]*\\.?[0-9]*"
                : "[0-9]*",
              min: allowNegativeValue ? undefined : 0,
            }),
          },
          style: {
            height: "36.75px",
            borderRadius,
            fontSize: fontSize,
            fontWeight: "500",
          },
        }}
        onWheel={(e) => {
          if (type === "number") {
            e.preventDefault();
            (e.target as HTMLElement).blur();
          }
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: borderColor || "#E0E0E0",
              borderRadius,
            },
            "&:hover fieldset": {
              borderColor: "#6455ED",
            },
            "&.Mui-focused": {
              backgroundColor: "#fff",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#6455ED",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.customColors.greyBodyBackground,
            },
          },
          "& .MuiInputBase-input": {
            color: "#000",
            fontSize,
            padding: "8px 14px",
          },
          "& .MuiInputAdornment-root": {
            color: "#A3A3A3",
          },
          "& .Mui-focused .MuiOutlinedInput-input": {
            backgroundColor: theme.palette.mode === "dark" ? "" : "#fff",
          },
        }}
      />
      {helperText && (
        <CustomTypography variant="body2" color={theme.palette.error.main}>
          {helperText}
        </CustomTypography>
      )}
    </div>
  );
};

export default CustomTextField;
